import React, {useEffect} from 'react'
import {logger} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {ApiUiCategory} from '@api/model/ui'
import {UIElement} from '@api/model/ui-element'
import {UIRegistry} from '@api/model/ui-registry'
import {globals} from '@extensions/globals-base'

export interface ApiUiProps {
  category: ApiUiCategory
  id?: DataObjectId
}

export const ApiUi = (props: ApiUiProps) => {
  const {category, id} = props
  const dataUi: ApiUiCategory = category.includes(':')
    ? category
    : `${category}:${id}`
  const ref = React.useRef<HTMLDivElement>(null)
  const mountedElementsRef = React.useRef<Record<string, UIElement>>({})
  const companyId = globals.getCompanyId()

  useEffect(() => {
    const registry = UIRegistry.get(companyId)

    const renderCategory = (key: ApiUiCategory) =>
      setupRenderer(key, ref, category, registry, id, mountedElementsRef)

    renderCategory(category)
    renderCategory(dataUi)
    const categoryListener = registry.addListener(category, () => {
      renderCategory(category)
    })
    const dataUiListener = registry.addListener(dataUi, () => {
      renderCategory(dataUi)
    })

    return () => {
      for (const currentKey in mountedElementsRef.current) {
        mountedElementsRef.current[currentKey].onUnmount?.()
      }
      registry.removeListener(categoryListener)
      registry.removeListener(dataUiListener)
    }
  }, [companyId, id, category])

  return <div data-ui={dataUi} ref={ref}></div>
}

function setupRenderer(
  key: ApiUiCategory,
  ref: React.RefObject<HTMLDivElement>,
  category: ApiUiCategory,
  reg: UIRegistry,
  id: DataObjectId | undefined,
  mountedElementRef: React.MutableRefObject<Record<string, UIElement>>
) {
  if (!id) {
    return
  }
  const renderer = reg.uiRenderers.get(key)
  if (!renderer) {
    clearElement(key, ref, mountedElementRef)
    return
  }
  try {
    const el = renderer(id)
    if (!el) {
      clearElement(key, ref, mountedElementRef)
      return
    }
    if (!(el instanceof UIElement)) {
      logger.error(
        `UI.add function must return UIElement, in category ${category}, id ${id}`
      )
      return
    }
    if (ref.current) {
      if (mountedElementRef.current[key]) {
        mountedElementRef.current[key].onUnmount?.()
      }
      mountedElementRef.current[key] = el
      el.render?.(ref.current)
    }
  } catch (e) {
    logger.error(`Error in UI renderer for ${category}`, e)
  }
}

function clearElement(
  key: ApiUiCategory,
  ref: React.RefObject<HTMLDivElement>,
  mountedElementRef: React.MutableRefObject<Record<string, UIElement>>
) {
  if (mountedElementRef.current[key]) {
    mountedElementRef.current[key].onUnmount?.()
    delete mountedElementRef.current[key]
  }
  if (ref.current) {
    if (
      !mountedElementRef.current[key] ||
      !mountedElementRef.current[key].isDrivenByReact
    ) {
      ref.current.innerHTML = ''
    }
    ref.current.className = ''
    ref.current.style.cssText = ''
  }
}
